import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isValidSubdomain, checkSubdomainAvailability } from '../services/subdomainService';
import { registerSubdomainAuto } from '../services/autoSubdomainService';
import { generateAndSaveSubdomainHTML } from '../services/htmlGeneratorService';
import Header from '../components/Header';
import styles from '../styles/pages/SubdomainRegistrationPage.module.scss';

const SubdomainRegistrationPage = () => {
    const { currentUser, accountId } = useAuth();
    const navigate = useNavigate();
    const [subdomain, setSubdomain] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [availability, setAvailability] = useState(null);
    const [isChecking, setIsChecking] = useState(false);

    useEffect(() => {
        if (!currentUser) {
            navigate('/login');
            return;
        }
    }, [currentUser, navigate]);

    const handleSubdomainChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSubdomain(value);
        setError('');
        setAvailability(null);

        // リアルタイムで形式チェック
        if (value && !isValidSubdomain(value)) {
            setError('サブドメインは英数字とハイフンのみ使用可能です（3-63文字）');
        }
    };

    const checkAvailability = async () => {
        if (!subdomain || !isValidSubdomain(subdomain)) {
            return;
        }

        setIsChecking(true);
        try {
            const isAvailable = await checkSubdomainAvailability(subdomain);
            setAvailability(isAvailable);
        } catch (error) {
            setError('利用可能性のチェックに失敗しました');
        } finally {
            setIsChecking(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subdomain || !isValidSubdomain(subdomain)) {
            setError('有効なサブドメインを入力してください');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            console.log('🚀 自動サブドメイン登録を開始:', subdomain);

            // 自動承認方式でサブドメインを登録
            const result = await registerSubdomainAuto(subdomain, currentUser.uid, currentUser.email);

            if (result.success) {
                // サブドメイン登録後、HTMLファイルを生成
                try {
                    await generateAndSaveSubdomainHTML(subdomain);
                    console.log('✅ サブドメインHTMLファイルが生成されました');
                } catch (htmlError) {
                    console.error('⚠️ サブドメインHTMLファイル生成エラー:', htmlError);
                    // HTMLファイル生成に失敗してもサブドメイン登録は成功とする
                }

                // 登録成功後、ダッシュボードにリダイレクト
                navigate(`/${accountId}/dashboard`, {
                    state: {
                        message: result.message,
                        subdomain: subdomain,
                        success: true
                    }
                });
            } else {
                setError(result.error || 'サブドメインの登録に失敗しました');
            }
        } catch (error) {
            console.error('❌ サブドメイン登録エラー:', error);
            setError(error.message || 'サブドメインの登録に失敗しました');
        } finally {
            setIsLoading(false);
        }
    };

    const getAvailabilityMessage = () => {
        if (isChecking) {
            return <span className={styles.checking}>チェック中...</span>;
        }

        if (availability === true) {
            return <span className={styles.available}>✓ 利用可能</span>;
        }

        if (availability === false) {
            return <span className={styles.unavailable}>✗ 既に使用されています</span>;
        }

        return null;
    };

    if (!currentUser) {
        return null;
    }

    return (
        <div>
            <Header />
            <div className={styles.container}>
                <div className={styles.registrationBox}>
                    <h1>サブドメイン登録</h1>
                    <p className={styles.description}>
                        あなた専用のサブドメインを登録して、ウェブサイトを公開しましょう。
                        <br />
                        <strong>自動承認方式</strong>：登録後すぐに利用可能になります！
                        {import.meta.env.DEV && (
                            <div style={{
                                marginTop: '10px',
                                padding: '10px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffeeba',
                                borderRadius: '4px',
                                color: '#856404'
                            }}>
                                <strong>開発環境</strong>：API呼び出しはシミュレーションされます。実際のDNS/ドメイン設定は行われません。
                            </div>
                        )}
                    </p>

                    {error && (
                        <div className={styles.error}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="subdomain">サブドメイン名</label>
                            <div className={styles.subdomainInput}>
                                <input
                                    type="text"
                                    id="subdomain"
                                    value={subdomain}
                                    onChange={handleSubdomainChange}
                                    placeholder="myshop"
                                    maxLength={63}
                                    disabled={isLoading}
                                />
                                <span className={styles.domainSuffix}>.suisui.life</span>
                            </div>
                            {getAvailabilityMessage()}
                        </div>

                        <div className={styles.examples}>
                            <h3>例:</h3>
                            <ul>
                                <li>myshop.suisui.life</li>
                                <li>my-blog.suisui.life</li>
                                <li>company2024.suisui.life</li>
                            </ul>
                        </div>

                        <div className={styles.rules}>
                            <h3>サブドメインのルール:</h3>
                            <ul>
                                <li>3〜63文字の英数字とハイフン</li>
                                <li>ハイフンで始まらない、終わらない</li>
                                <li>連続するハイフンは使用不可</li>
                                <li>一度登録すると変更不可</li>
                            </ul>
                        </div>

                        <div className={styles.actions}>
                            <button
                                type="button"
                                onClick={checkAvailability}
                                disabled={!subdomain || !isValidSubdomain(subdomain) || isChecking}
                                className={styles.checkButton}
                            >
                                利用可能性をチェック
                            </button>

                            <button
                                type="submit"
                                disabled={!subdomain || !isValidSubdomain(subdomain) || isLoading || availability === false}
                                className={styles.submitButton}
                            >
                                {isLoading ? '登録中...' : 'サブドメインを登録'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SubdomainRegistrationPage; 